import React, { useState } from 'react';
import { useInquiry } from '../../context/InquiryContext';
import { getPlanConfig } from '../../utils/membershipUtils';
import { useCurrency } from '../../context/CurrencyContext';
import './ProductDetailsModal.css';
import { PRODUCT_PLACEHOLDER, LOGO_PLACEHOLDER } from '../../utils/placeholders';

const ProductDetailsModal = ({ isOpen, onClose, product }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const { addToInquiry, isInInquiry, removeFromInquiry } = useInquiry();
  const { formatPriceRange, formatPrice } = useCurrency();
  const [showAdded, setShowAdded] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);

  if (!isOpen || !product) return null;

  // Get seller data - from product.seller (database relation)
  const seller = product.seller || {};
  
  // Map product data from database format - use ONLY real data from the database
  const mapProductData = (p) => {
    if (!p) return null;
    
    // Parse images if it's a JSON string
    let additionalImages = [];
    if (p.images) {
      try {
        additionalImages = typeof p.images === 'string' ? JSON.parse(p.images) : p.images;
      } catch (e) {
        additionalImages = [];
      }
    }
    
    return {
      id: p.id,
      // Product info
      title: p.name || p.description || p.title || '',
      name: p.name || '',
      description: p.description || '',
      price: (p.priceMin !== undefined && p.priceMax !== undefined) 
        ? formatPriceRange(p.priceMin, p.priceMax)
        : (p.price ? formatPrice(p.price) : ''),
      priceMin: p.priceMin,
      priceMax: p.priceMax,
      unit: p.unit || p.pricePer || '',
      moq: p.moq || '',
      
      // Images
      mainImage: p.image || p.mainImage || '',
      thumbnails: additionalImages,
      
      // Seller info from database relation
      companyName: seller.name || '',
      country: seller.country || '',
      countryFlag: seller.countryFlag || '',
      memberYears: seller.memberYears || 0,
      businessType: seller.businessType || '',
      isVerified: seller.isVerified || false,
      isGlobal: seller.isGlobal || false,
      sellerId: seller.userId || p.userId || seller.id,
      sellerAvatar: seller.logo || '',
      sellerPhone: seller.phoneNumber || seller.phone || '',
      sellerEmail: seller.email || '',
      sellerAddress: seller.address || '',
      sellerCity: seller.city || '',
      sellerDescription: seller.description || seller.companyOverview || '',
      sellerNotes: seller.notes || p.sellerNotes || '',
      
      // Category info
      categoryName: p.category?.name || '',
      subcategoryName: p.subcategory?.name || '',
      
      // Brand info
      brandName: p.brand?.name || '',
      brandLogo: p.brand?.logo || '',
      
      // Additional info
      keyFeatures: p.keyFeatures || [],
      deliveryTime: p.deliveryTime || '7-15 business days',
      paymentTerms: p.paymentTerms || 'T/T, L/C, Western Union',
      supplyAbility: p.supplyAbility || '',
    };
  };

  const data = mapProductData(product);
  
  if (!data) return null;
  const isAdded = isInInquiry(data.id);
  
  // Get all images for the gallery
  const allImages = [data.mainImage, ...(data.thumbnails || [])].filter(Boolean);
  const currentImage = allImages[activeImageIndex] || data.mainImage;

  // Default key features if none provided
  const keyFeatures = data.keyFeatures.length > 0 ? data.keyFeatures : [
    'High Quality Material',
    'Durable Construction',
    'Industry Standard',
    'Easy Installation',
    'Long Warranty Period',
    'Eco-Friendly'
  ];

  const handleInquireClick = () => {
    if (isAdded) {
      removeFromInquiry(data.id);
    } else {
      addToInquiry(product || data);
      setShowAdded(true);
      setTimeout(() => setShowAdded(false), 2000);
    }
  };

  const handleWishlistClick = () => {
    setIsWishlisted(!isWishlisted);
  };

  return (
    <div className="pdm-overlay" onClick={onClose}>
      <div className="pdm-container" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="pdm-close-btn" onClick={onClose} aria-label="Close modal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="#FF0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="#FF0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div className="pdm-layout">
          {/* Left Sidebar - Seller Info */}
          <div className="pdm-sidebar">
            <div className="pdm-seller-card">
              <div className="pdm-seller-avatar">
                <img 
                  src={data.sellerAvatar || LOGO_PLACEHOLDER} 
                  alt={data.companyName}
                  onError={(e) => { e.target.src = LOGO_PLACEHOLDER; }}
                />
              </div>
              <h3 className="pdm-seller-name">{data.companyName || 'Seller'}</h3>
              <p className="pdm-seller-location">{data.sellerCity || data.country}</p>
              
              <div className="pdm-seller-badges">
                {data.isVerified ? (
                  <span className="pdm-badge pdm-badge-verified">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <polyline points="22 4 12 14.01 9 11.01" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Verified
                  </span>
                ) : (
                  <span className="pdm-badge pdm-badge-unverified">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="#DC2626" strokeWidth="2"/>
                      <line x1="12" y1="8" x2="12" y2="12" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"/>
                      <circle cx="12" cy="16" r="1" fill="#DC2626"/>
                    </svg>
                    Unverified Supplier
                  </span>
                )}
                {(() => {
                  const planConfig = getPlanConfig(data.membershipPlan || seller.membershipPlan);
                  return (
                    <span className="pdm-badge pdm-badge-global" style={{ backgroundColor: planConfig.color }}>
                      {planConfig.label}
                    </span>
                  );
                })()}
              </div>

              <div className="pdm-seller-stats">
                <div className="pdm-stat">
                  <span className="pdm-stat-value">{data.memberYears || 1}</span>
                  <span className="pdm-stat-label">Years Member</span>
                </div>
                <div className="pdm-stat">
                  <span className="pdm-stat-value">{data.businessType || 'Manufacturer'}</span>
                  <span className="pdm-stat-label">Business Type</span>
                </div>
              </div>

              <div className="pdm-seller-menu">
                <a href="#overview" className="pdm-menu-item active">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Overview
                </a>
                <a href="#products" className="pdm-menu-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" stroke="currentColor" strokeWidth="2"/>
                    <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Products
                </a>
                <a href="#contact" className="pdm-menu-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Contact
                </a>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="pdm-main">
            {/* Product Header Section */}
            <div className="pdm-product-header">
              {/* Image Gallery */}
              <div className="pdm-gallery">
                <div className="pdm-main-image">
                  <img 
                    src={currentImage} 
                    alt={data.title}
                    onError={(e) => { e.target.src = PRODUCT_PLACEHOLDER; }}
                  />
                </div>
                <div className="pdm-thumbnails">
                  {allImages.slice(0, 5).map((thumb, index) => (
                    <div 
                      key={index}
                      className={`pdm-thumb ${activeImageIndex === index ? 'active' : ''}`}
                      onClick={() => setActiveImageIndex(index)}
                    >
                      <img 
                        src={thumb} 
                        alt={`View ${index + 1}`}
                        onError={(e) => { e.target.src = PRODUCT_PLACEHOLDER; }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Product Info */}
              <div className="pdm-product-info">
                <h1 className="pdm-product-title">{data.title}</h1>
                
                <div className="pdm-price-section">
                  <span className="pdm-price">{data.price || 'Contact for Price'}</span>
                  {data.unit && <span className="pdm-price-unit">/ {data.unit}</span>}
                </div>
                
                {data.moq && (
                  <p className="pdm-moq">Minimum Order: <strong>{data.moq}</strong></p>
                )}

                {/* Action Buttons */}
                <div className="pdm-actions">
                  <button 
                    className={`pdm-btn-wishlist ${isWishlisted ? 'active' : ''}`}
                    onClick={handleWishlistClick}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill={isWishlisted ? "currentColor" : "none"}>
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {isWishlisted ? 'Added to Wishlist' : 'Add to Wishlist'}
                  </button>
                  
                  <button 
                    className={`pdm-btn-inquiry ${isAdded ? 'added' : ''}`}
                    onClick={handleInquireClick}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {showAdded ? '✓ Added!' : isAdded ? 'Remove from Inquiry' : 'Add to Inquiry'}
                  </button>
                </div>
              </div>
            </div>

            {/* Product Overview Section */}
            <div className="pdm-section">
              <h2 className="pdm-section-title">Product Overview</h2>
              <p className="pdm-description">
                {data.description || 'No description available for this product.'}
              </p>
            </div>

            {/* Key Features Section */}
            <div className="pdm-section">
              <h2 className="pdm-section-title">Key Features</h2>
              <div className="pdm-features-grid">
                {keyFeatures.map((feature, index) => (
                  <div key={index} className="pdm-feature-item">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="3" width="18" height="18" rx="2" stroke="#29ABE2" strokeWidth="2"/>
                      <path d="M9 12l2 2 4-4" stroke="#29ABE2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Delivery / Payment Section */}
            <div className="pdm-section">
              <h2 className="pdm-section-title">Delivery / Payment</h2>
              <div className="pdm-delivery-grid">
                <div className="pdm-delivery-item">
                  <span className="pdm-delivery-label">Delivery Time:</span>
                  <span className="pdm-delivery-value">{data.deliveryTime}</span>
                </div>
                <div className="pdm-delivery-item">
                  <span className="pdm-delivery-label">Payment Terms:</span>
                  <span className="pdm-delivery-value">{data.paymentTerms}</span>
                </div>
                {data.supplyAbility && (
                  <div className="pdm-delivery-item">
                    <span className="pdm-delivery-label">Supply Ability:</span>
                    <span className="pdm-delivery-value">{data.supplyAbility}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Seller's Notes Section */}
            <div className="pdm-section">
              <h2 className="pdm-section-title">Seller's Notes</h2>
              <div className="pdm-seller-notes">
                <p>{data.sellerNotes || data.sellerDescription || 'Contact us for more information about this product. We are happy to assist with any questions regarding specifications, bulk orders, or custom requirements.'}</p>
              </div>
            </div>

            {/* Request Quote Button */}
            <div className="pdm-quote-section">
              <button className="pdm-btn-quote" onClick={handleInquireClick}>
                Request Quote
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsModal;
